/**
 * GuideArticlePage — one help-centre article at /help/guides/:category/:slug.
 */

import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Anchor, Grid, Space, Tag, Typography } from "antd";
import Layout from "../../../components/Layout";
import { PageContainer, SectionCard, EmptyState } from "../../../components/shared";
import { articles, categoryDefs } from "../../../content/help";
import type { DocHeading } from "../../../content/help";
import {
  visibleArticles,
  findArticle,
  findRelated,
  siblingArticles,
} from "../../../content/help/model";
import { isPatientPortalUser } from "../../../utils/userPrefs/validatePermissions";
import GuideMarkdown from "./GuideMarkdown";

const { Text } = Typography;

type TocItem = { key: string; href: string; title: string; children?: TocItem[] };

/** h2s as top-level entries, h3s nested under the h2 they follow. */
function tocItems(headings: DocHeading[]): TocItem[] {
  const items: TocItem[] = [];
  for (const h of headings) {
    const item: TocItem = { key: h.id, href: `#${h.id}`, title: h.text };
    const parent = items[items.length - 1];
    if (h.depth === 3 && parent) {
      parent.children = [...(parent.children ?? []), item];
    } else {
      items.push(item);
    }
  }
  return items;
}

const GuideArticlePage: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();

  const viewerIsPatient = isPatientPortalUser();
  const visible = useMemo(() => visibleArticles(articles, viewerIsPatient), [viewerIsPatient]);
  const article = category && slug ? findArticle(visible, category, slug) : undefined;

  if (!article) {
    return (
      <Layout>
        <PageContainer
          title="User Guides"
          breadcrumbs={[{ label: "Help & Support", path: "/help" }, { label: "User Guides" }]}
          data-testid="help-guides-article"
        >
          <EmptyState
            title="Guide not found"
            description="It may have moved, or it isn't available on your account."
            action={{ label: "Back to guides", onClick: () => navigate("/help/guides") }}
          />
        </PageContainer>
      </Layout>
    );
  }

  const categoryTitle = categoryDefs.find((c) => c.slug === article.category)?.title ?? article.category;
  const related = findRelated(visible, article);
  const { prev, next } = siblingArticles(visible, article);
  const showToc = Boolean(screens.xl) && article.headings.length >= 2;

  return (
    <Layout>
      <PageContainer
        title={article.title}
        subtitle={article.summary}
        breadcrumbs={[
          { label: "Help & Support", path: "/help" },
          { label: "User Guides", path: "/help/guides" },
          { label: categoryTitle, path: `/help/guides/${article.category}` },
          { label: article.title },
        ]}
        data-testid="help-guides-article"
      >
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
          <SectionCard style={{ flex: 1, minWidth: 0, maxWidth: 860 }}>
            <Space size={8} wrap style={{ marginBottom: 16 }}>
              <Tag color="blue">{article.type.replace("-", " ")}</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Last reviewed {article.updated}
              </Text>
            </Space>

            <GuideMarkdown text={article.body} />

            {related.length > 0 && (
              <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid var(--mg-border)" }}>
                <Text strong style={{ fontSize: 12 }}>
                  See also
                </Text>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {related.map((r) => (
                    <li key={r.path} style={{ marginBottom: 4 }}>
                      <Link to={r.path}>{r.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(prev || next) && (
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 12,
                  borderTop: "1px solid var(--mg-border)",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                {prev ? (
                  <Link to={prev.path} data-testid="help-guides-prev">
                    <Text type="secondary" style={{ fontSize: 10, textTransform: "uppercase", display: "block" }}>
                      Previous
                    </Text>
                    {prev.title}
                  </Link>
                ) : (
                  <span />
                )}
                {next ? (
                  <Link to={next.path} data-testid="help-guides-next" style={{ textAlign: "right" }}>
                    <Text type="secondary" style={{ fontSize: 10, textTransform: "uppercase", display: "block" }}>
                      Next
                    </Text>
                    {next.title}
                  </Link>
                ) : (
                  <span />
                )}
              </div>
            )}
          </SectionCard>

          {showToc && (
            <div style={{ width: 220, flexShrink: 0 }}>
              <Anchor targetOffset={80} items={tocItems(article.headings)} />
            </div>
          )}
        </div>
      </PageContainer>
    </Layout>
  );
};

export default GuideArticlePage;
